import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { 
  Bold, 
  Italic, 
  Heading1, 
  Heading2, 
  List, 
  ListOrdered, 
  Quote, 
  Link as LinkIcon,
  Code
} from 'lucide-react';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export const RichTextEditor = ({ content, onChange, placeholder = 'Write something amazing...' }: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rounded-2xl max-w-full h-auto my-8',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-indigo max-w-none focus:outline-none min-h-[300px] text-gray-900',
      },
    },
  });

  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="relative border border-gray-200 rounded-2xl bg-white overflow-hidden transition-all focus-within:ring-4 focus-within:ring-indigo-500/10 focus-within:border-indigo-500">
      {/* Top Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-gray-100 bg-gray-50/50 sticky top-0 z-10 overflow-x-auto">
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleBold().run()} 
          active={editor.isActive('bold')}
          icon={<Bold className="w-4 h-4" />}
          label="Bold"
        />
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleItalic().run()} 
          active={editor.isActive('italic')}
          icon={<Italic className="w-4 h-4" />}
          label="Italic"
        />
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} 
          active={editor.isActive('heading', { level: 1 })}
          icon={<Heading1 className="w-4 h-4" />}
          label="H1"
        />
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} 
          active={editor.isActive('heading', { level: 2 })}
          icon={<Heading2 className="w-4 h-4" />}
          label="H2"
        />
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleBulletList().run()} 
          active={editor.isActive('bulletList')}
          icon={<List className="w-4 h-4" />}
          label="Bullet List"
        />
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleOrderedList().run()} 
          active={editor.isActive('orderedList')}
          icon={<ListOrdered className="w-4 h-4" />}
          label="Ordered List"
        />
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleBlockquote().run()} 
          active={editor.isActive('blockquote')}
          icon={<Quote className="w-4 h-4" />}
          label="Quote"
        />
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleCodeBlock().run()} 
          active={editor.isActive('codeBlock')}
          icon={<Code className="w-4 h-4" />}
          label="Code"
        />
        <ToolbarButton 
          onClick={setLink} 
          active={editor.isActive('link')}
          icon={<LinkIcon className="w-4 h-4" />}
          label="Link"
        />
      </div>

      {/* Bubble Menu */}
      {editor && (
        <BubbleMenu editor={editor}>
          <div className="flex items-center gap-0.5 p-1 bg-gray-900 rounded-xl shadow-2xl border border-gray-800">
            <BubbleButton 
              onClick={() => editor.chain().focus().toggleBold().run()} 
              active={editor.isActive('bold')}
              icon={<Bold className="w-3.5 h-3.5" />}
            />
            <BubbleButton 
              onClick={() => editor.chain().focus().toggleItalic().run()} 
              active={editor.isActive('italic')}
              icon={<Italic className="w-3.5 h-3.5" />}
            />
            <div className="w-px h-3 bg-gray-700 mx-0.5" />
            <BubbleButton 
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} 
              active={editor.isActive('heading', { level: 2 })}
              icon={<Heading2 className="w-3.5 h-3.5" />}
            />
            <BubbleButton 
              onClick={setLink} 
              active={editor.isActive('link')}
              icon={<LinkIcon className="w-3.5 h-3.5" />}
            />
          </div>
        </BubbleMenu>
      )}

      {/* Editor Surface */}
      <div className="p-4">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

const ToolbarButton = ({ onClick, active, icon, label }: { onClick: () => void, active: boolean, icon: React.ReactNode, label: string }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    className={`p-2 rounded-lg transition-all flex items-center justify-center ${
      active ? 'bg-indigo-100 text-indigo-600 shadow-sm' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
    }`}
  >
    {icon}
  </button>
);

const BubbleButton = ({ onClick, active, icon }: { onClick: () => void, active: boolean, icon: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className={`p-1.5 rounded-lg transition-all flex items-center justify-center ${
      active ? 'text-indigo-400 bg-white/10' : 'text-gray-300 hover:bg-white/10 hover:text-white'
    }`}
  >
    {icon}
  </button>
);
